import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { AppPermissionService } from '@core/services/app-permission.service';
import { BlacklistTemplateService } from '../../../promotion-codes/services/blacklist-template.service';

@Component({
  selector: 'kt-promotion',
  templateUrl: './provider-blacklist-setting.component.html',
  styleUrls: ['./provider-blacklist-setting.component.scss']
})
export class ProviderBlacklistSettingComponent implements OnInit, OnDestroy {

  // Slots category only for free spin (matches the previous getBlacklistedData(['Slots']) scope)
  private static readonly CATEGORY_CODES = ['SL'];

  isFreeSpin = false;
  loading = false;
  private savePresetSub: Subscription;

  currencies: any[] = [];
  activeCurrencyId = 0;

  // Nested tree keyed by currency id (from /blacklist/promotion -> Blacklist::getTemplate):
  // tree[currencyId] = [{ game_provider_id, game_provider_code,
  //                       categories: [{ id, code, name, sub_categories: [{ id, name, status }] }] }]
  tree: Record<number, any[]> = {};
  // The promotion's Target game providers — the blacklist is scoped to these only (matches v1)
  private targetProviderIds: number[] = [];
  activeProviderId: number = null;
  searchProvider = '';

  // Blacklist presets (templates) — rendered with kt-dropdown-wo-lazyload (single-select) so it
  // matches the other wizard dropdowns (SMS/Message/Provider). The widget writes to presetForm; we
  // mirror the choice into selectedPresetId via onPresetSelected.
  presetList: any[] = [];
  selectedPresetId: number = null;
  presetForm: FormGroup = new FormGroup({ preset_id: new FormControl(null) });
  presetSelectedItems: any[] = [];
  presetDropdownSettings = {
    singleSelection: true,
    text: 'Please Select',
    enableSearchFilter: true,
    classes: 'dropdown',
    primaryKey: 'id',
    labelKey: 'name',
    noDataLabel: 'No presets',
    showCheckbox: false,
    disabled: false,
  };
  presetLoading = false;   // spinner + disabled state while the preset list loads
  presetName = '';
  feedbackMsg = '';
  savingPreset = false;
  presetLocked = false; // true after a preset is applied — the blacklist is read-only until the preset is removed (matches v1)
  // Editing the game provider blacklist requires edit_game_provider_blacklist (matches v1). Without it the
  // step is view-only: presets can't be applied/removed, categories can't be toggled, presets can't be saved.
  canEditBlacklist = false;

  constructor(
    @Optional() @Inject('initialData') private initialData: any,
    private eventEmitterService: EventEmitterService,
    private blacklistTemplateService: BlacklistTemplateService,
    private appPermissionService: AppPermissionService,
  ) {
    this.initialData = initialData;
  }

  ngOnInit() {
    this.isFreeSpin = this.initialData?.promotion_type == 'Free Spin';
    // Free Spin supports all currencies; PP / BTG are limited to MYR / AUD.
    const allCurrencies = JSON.parse(sessionStorage.getItem('currencies')) || [];
    this.currencies = this.isFreeSpin ? allCurrencies : allCurrencies.filter((c: any) => ['MYR', 'AUD'].includes(c.name));
    if (this.currencies.length > 0) this.activeCurrencyId = this.currencies[0].id;
    if (this.isFreeSpin) {
      this.appPermissionService.getAppPermissions().pipe(take(1))
        .subscribe(perms => this.canEditBlacklist = !!perms.edit_game_provider_blacklist);
      this.loadBlacklist();
      this.loadPresets();
      // "Save as Preset" lives in the wizard footer (parent) — it triggers this component via the event bus
      this.savePresetSub = this.eventEmitterService.saveBlacklistPreset.subscribe(() => this.saveAsPreset());
    }
    // NOTE: no emit here — emitting before the tree loads would push an empty list to the parent and
    // clobber the persisted selection. We emit only after fetchTree() completes.
  }

  ngOnDestroy() {
    this.savePresetSub?.unsubscribe();
  }

  private loadBlacklist() {
    this.loading = true;
    // Scope the blacklist to the promotion's Target game providers (Eligibility -> Target Amount),
    // exactly like v1's blacklist. Only those providers can be blacklisted.
    this.targetProviderIds = this.getTargetProviderIds();
    // null blacklist_id -> statuses reflect the promotion's own current blacklist (edit) or all-off (create).
    // applyPersisted=true so an in-progress selection survives leaving and returning to this step.
    this.fetchTree(null, undefined, true);
  }

  // The promotion's Target game providers, from the Eligibility step's target selection.
  // Free Spin: that target is just a mirror of Basic Setting's Free Spin Game Provider, so fall back
  // to it directly — the operator can reach this step without opening Eligibility, and without the
  // fallback the list would render empty even though a provider is already chosen.
  private getTargetProviderIds(): number[] {
    const target = this.initialData?.target;
    if (Array.isArray(target) && target.length > 0 && Array.isArray(target[0]?.game_provider_ids)) {
      return target[0].game_provider_ids.map((id: any) => Number(id));
    }
    if (Array.isArray(this.initialData?.game_provider_ids) && this.initialData.game_provider_ids.length > 0) {
      return this.initialData.game_provider_ids.map((id: any) => Number(id));
    }
    const freeSpinProviderId = this.initialData?.free_spin_game_provider_id;
    return freeSpinProviderId ? [Number(freeSpinProviderId)] : [];
  }

  // True once a provider is chosen (Basic Setting -> Free Spin Game Provider). Drives the empty-state
  // hint: nothing can ever be listed here until that field is filled in.
  hasTargetProvider(): boolean {
    return this.targetProviderIds.length > 0;
  }

  // Load the provider/category/sub-category tree with statuses.
  // blacklistId != null  -> statuses come from that preset (template)
  // blacklistId === null -> statuses come from the promotion's existing blacklist
  private fetchTree(blacklistId: number | null, successMsg?: string, applyPersisted = false, lockOnSuccess = false) {
    this.loading = true;
    this.blacklistTemplateService.getGameProviderByPromotion({
      promotion_id: blacklistId != null ? null : (this.initialData?.id ?? null),
      blacklist_id: blacklistId,
      currency_ids: this.currencies.map((c: any) => c.id),
      category_codes: ProviderBlacklistSettingComponent.CATEGORY_CODES,
      game_provider_ids: this.targetProviderIds,
    }).subscribe({
      next: (res: any) => {
        this.tree = res || {};
        // Restore the selection the user already made (persisted in the parent) so it isn't lost
        // when returning to this step. Skipped for preset-apply, which uses the template's statuses.
        if (applyPersisted) this.applyPersistedSelection();
        // keep the current provider selected if it still exists, otherwise pick the first
        const providers = this.providersForActiveCurrency();
        if (!providers.some(p => p.game_provider_id === this.activeProviderId)) {
          this.activeProviderId = providers[0] ? providers[0].game_provider_id : null;
        }
        this.loading = false;
        this.emit();
        if (lockOnSuccess) this.presetLocked = true; // lock only after the preset actually loaded
        if (successMsg) this.flash(successMsg);
      },
      error: (err: any) => {
        this.loading = false;
        if (lockOnSuccess) {
          // preset-apply failed → keep the current tree and stay unlocked/editable
          this.presetLocked = false;
          this.flash(this.apiError(err, 'Failed to apply preset.'));
        } else {
          // Initial-load failure: keep the promotion's saved blacklist in the parent. Do NOT emit an
          // empty list — updateV4 deletes every saved sub-category row whenever black_list_sub_categories
          // is present, so emitting [] here would wipe the operator's saved blacklist on a transient error.
          this.tree = {};
          this.flash(this.apiError(err, 'Failed to load blacklist.'));
        }
      },
    });
  }

  // Re-apply the selection persisted in the parent (formData.black_list_sub_categories) on top of the
  // freshly-loaded tree. Authoritative when defined: an empty list means "everything unticked".
  // Undefined (never emitted yet) means keep the API-loaded statuses (the promotion's saved blacklist on edit).
  private applyPersistedSelection() {
    const persisted = this.initialData?.black_list_sub_categories;
    if (!Array.isArray(persisted)) return;
    const keys = new Set<string>();
    persisted.forEach((row: any) => (row.sub_categories || []).forEach((s: any) =>
      keys.add(`${row.settings_currency_id}:${row.game_provider_id}:${s.name}`)));
    Object.keys(this.tree).forEach(cidStr => {
      const cid = Number(cidStr);
      (this.tree[cid] || []).forEach(p =>
        (p.categories || []).forEach((c: any) =>
          (c.sub_categories || []).forEach((s: any) => {
            s.status = keys.has(`${cid}:${p.game_provider_id}:${s.name}`) ? 1 : 0;
          })));
    });
  }

  selectCurrency(id: number) {
    this.activeCurrencyId = id;
    const first = this.providersForActiveCurrency()[0];
    this.activeProviderId = first ? first.game_provider_id : null;
  }

  selectProvider(providerId: number) {
    this.activeProviderId = providerId;
  }

  // providers for the active currency (+ search filter)
  providersForActiveCurrency(): any[] {
    const list = this.tree[this.activeCurrencyId] || [];
    const q = (this.searchProvider || '').toLowerCase().trim();
    return q ? list.filter(p => (p.game_provider_code || '').toLowerCase().includes(q)) : list;
  }

  private activeProvider(): any {
    return (this.tree[this.activeCurrencyId] || []).find(p => p.game_provider_id === this.activeProviderId) || null;
  }

  // category rows (with their sub_categories) for the active provider + currency
  categoriesForActive(): any[] {
    return this.activeProvider()?.categories || [];
  }

  toggleSubCategory(row: any, sub: any) {
    if (this.presetLocked) return; // locked while a preset is applied
    sub.status = sub.status === 1 ? 0 : 1;
    this.emit();
  }

  toggleCategory(row: any) {
    if (this.presetLocked) return; // locked while a preset is applied
    const allChecked = this.isCategoryChecked(row);
    (row.sub_categories || []).forEach((s: any) => s.status = allChecked ? 0 : 1);
    this.emit();
  }

  isCategoryChecked(row: any): boolean {
    return (row.sub_categories || []).length > 0 && row.sub_categories.every((s: any) => s.status === 1);
  }

  // info icon + bold when a provider (in the active currency) has any blacklisted sub-category
  providerHasSelection(providerId: number, currencyId: number = this.activeCurrencyId): boolean {
    const provider = (this.tree[currencyId] || []).find(p => p.game_provider_id === providerId);
    return !!provider && (provider.categories || []).some((c: any) =>
      (c.sub_categories || []).some((s: any) => s.status === 1));
  }

  // copy the active currency's selections to every other loaded currency (sub-category ids are shared across currencies)
  applyToOtherCurrencies() {
    if (this.presetLocked) return; // locked while a preset is applied
    const source = new Map<string, number>();
    (this.tree[this.activeCurrencyId] || []).forEach(p =>
      (p.categories || []).forEach((c: any) =>
        (c.sub_categories || []).forEach((s: any) => source.set(`${p.game_provider_id}:${s.id}`, s.status))));

    this.currencies.forEach((cur: any) => {
      if (cur.id === this.activeCurrencyId) return;
      (this.tree[cur.id] || []).forEach(p =>
        (p.categories || []).forEach((c: any) =>
          (c.sub_categories || []).forEach((s: any) => {
            const v = source.get(`${p.game_provider_id}:${s.id}`);
            if (v !== undefined) s.status = v;
          })));
    });
    this.emit();
    const others = this.currencies.filter((c: any) => c.id !== this.activeCurrencyId).length;
    this.flash(others > 0 ? `Applied current selection to ${others} other currenc${others > 1 ? 'ies' : 'y'}.` : 'No other currencies to apply to.');
  }

  private flash(msg: string) {
    this.feedbackMsg = msg;
  }

  private loadPresets() {
    this.presetLoading = true;
    this.presetDropdownSettings = { ...this.presetDropdownSettings, disabled: true };
    const stopLoading = () => {
      this.presetLoading = false;
      this.presetDropdownSettings = { ...this.presetDropdownSettings, disabled: false };
    };
    this.blacklistTemplateService.getList('?paginate=false').subscribe({
      next: (res: any[]) => { this.presetList = res || []; stopLoading(); },
      error: (err: any) => { this.presetList = []; stopLoading(); this.flash(this.apiError(err, 'Failed to load presets.')); },
    });
  }

  // Apply a saved preset: confirm first, then reload the tree with the preset's blacklist_id so statuses reflect the template.
  applyPreset() {
    if (!this.selectedPresetId) { this.flash('Select a preset first.'); return; }
    Swal.fire({
      title: 'Apply Preset',
      html: 'Apply this preset? Its blacklisted categories will be loaded into the wizard, '
        + 'replacing your current selection for this promotion.',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Apply',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      reverseButtons: true,
    }).then(resp => {
      if (resp.isConfirmed) {
        // lockOnSuccess=true: lock only if the preset actually loads (fetchTree flips presetLocked in its next handler)
        this.fetchTree(this.selectedPresetId, 'Preset applied.', false, true);
      }
    });
  }

  // Mirror the dropdown selection into selectedPresetId (single-select → first item or none).
  onPresetSelected(items: any[]) {
    this.selectedPresetId = (items && items.length) ? items[0].id : null;
  }

  // Remove the applied preset: keep the current ticks, just unlock editing (matches v1's deselect behaviour).
  removePreset() {
    this.presetLocked = false;
    this.selectedPresetId = null;
    this.presetSelectedItems = [];
    this.presetForm.get('preset_id')?.patchValue(null);
    this.flash('Preset removed — you can edit the blacklist again.');
  }

  saveAsPreset() {
    if (!this.canEditBlacklist) { this.flash('You do not have permission to edit the game provider blacklist.'); return; }
    if (this.savingPreset) return;
    if (!this.presetName || !this.presetName.trim()) { this.flash('Enter a preset name first.'); return; }
    // API store() expects sub_categories = { currencyId: [gameProviderSubCategoryId, ...] } (flat int array per currency)
    const byCurrency: Record<number, number[]> = {};
    let hasSelection = false;
    Object.keys(this.tree).forEach(cidStr => {
      const cid = Number(cidStr);
      (this.tree[cid] || []).forEach(p =>
        (p.categories || []).forEach((c: any) =>
          (c.sub_categories || []).forEach((s: any) => {
            if (s.status !== 1 || s.id == null) return;
            hasSelection = true;
            byCurrency[cid] = byCurrency[cid] || [];
            if (!byCurrency[cid].includes(Number(s.id))) byCurrency[cid].push(Number(s.id));
          })));
    });
    if (!hasSelection) { this.flash('Select at least one category before saving a preset.'); return; }
    this.savingPreset = true;
    this.blacklistTemplateService.createTemplate({
      name: this.presetName.trim(),
      remarks: '',
      status: 1,
      sub_categories: byCurrency,
    } as any).subscribe({
      next: () => { this.presetName = ''; this.savingPreset = false; this.flash('Preset saved.'); this.loadPresets(); },
      error: (err: any) => { this.savingPreset = false; this.flash(this.apiError(err, 'Failed to save preset.')); },
    });
  }

  private apiError(err: any, fallback: string): string {
    const m = err?.error?.message ?? err?.message;
    if (Array.isArray(m)) return m.join(' ');
    return typeof m === 'string' ? m : fallback;
  }

  // build black_list_sub_categories (name-based, matches storeV4/updateV4) from every ticked sub-category
  private emit() {
    const data: any[] = [];
    Object.keys(this.tree).forEach(cidStr => {
      const cid = Number(cidStr);
      (this.tree[cid] || []).forEach(p => {
        const subs: any[] = [];
        (p.categories || []).forEach((c: any) =>
          (c.sub_categories || []).forEach((s: any) => { if (s.status === 1) subs.push({ name: s.name, status: 1 }); }));
        if (subs.length > 0) {
          data.push({ game_provider_id: p.game_provider_id, settings_currency_id: cid, sub_categories: subs });
        }
      });
    });
    this.eventEmitterService.onProviderBlacklistData({ black_list_sub_categories: data });
  }
}
